/**
 * ManageMcpModal — 520px. Server URL, transport, product type, discovered
 * tools (+ re-sync adds bulk_export), disconnect/save.
 */
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../theme';
import { Close, Refresh } from '../icons';
import { Button, Select } from '../components';
import { useBridge } from '../store/bridgeStore';
import { allInts, nameOf } from '../domain/catalog';
import { mcpToolsFor } from '../domain/ingest';
import { CATEGORIES } from '../data/seeds';

export default function ManageMcpModal() {
  const { state, actions } = useBridge();
  const m = state.mcpManage;
  if (!m) return null;
  const ints = allInts(state.adapters, state.metaOverrides);
  const a = (ints.find(i => i.id === m.id) || {}) as any;
  // Prefer the REAL tools discovered from the live server (m.tools, populated by
  // the store on open/resync). Fall back to the category mock ONLY when none were
  // loaded (demo/mock connector with no backend connection).
  const tools = m.tools && m.tools.length ? m.tools : mcpToolsFor(m.category, m.extraTool);
  const catOptions = CATEGORIES.map(c => ({ value: c, label: c }));

  return (
    <View>
      {React.createElement(
        'div',
        { className: 't-anim-fadein', onClick: actions.closeMcpManage, style: { position: 'fixed', inset: 0, background: colors.overlay, display: 'grid', placeItems: 'center', zIndex: 70, padding: 24 } },
        React.createElement(
          'div',
          { className: 't-anim-slideup', onClick: (e: React.MouseEvent) => e.stopPropagation(), style: { background: colors.card, borderRadius: 12, boxShadow: 'var(--shadow-lg)', width: 520, maxWidth: '100%', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' } },
          <Body key="b" m={m} a={a} tools={tools} loading={!!m.loading} catOptions={catOptions} actions={actions} ints={ints} />,
        ),
      )}
    </View>
  );
}

function Body({ m, a, tools, loading, catOptions, actions, ints }: any) {
  return (
    <>
      <View style={styles.header}>
        <View style={[styles.avatar, { backgroundColor: a.color || '#0a8a87' } as any]}><Text style={styles.avatarText}>{a.initials}</Text></View>
        <View style={{ flex: 1 }}>
          <Text style={styles.name}>{a.name || nameOf(m.id, ints)}</Text>
          <Text className="t-mono" style={styles.sub}>MCP adapter · self-describing</Text>
        </View>
        <View style={styles.connected}><View style={styles.connectedDot} /><Text style={styles.connectedText}>Connected</Text></View>
        <Pressable onPress={actions.closeMcpManage}><Close size={18} color={colors.ink3} /></Pressable>
      </View>

      <ScrollView style={{ maxHeight: '64vh' as any }} contentContainerStyle={{ padding: 20 }}>
        <View style={styles.infoBox}><Text style={styles.infoText}>MCP servers are self-describing — there's no field mapping to edit. Manage the connection, transport, and re-sync the tool list here.</Text></View>

        {/* Built-in/managed connectors (HubSpot/Zoho): the server URL is owned by
            the connector definition, not the tenant — so it's read-only here and
            the BE rejects edits too. The customer's only action is OAuth. Custom
            user-ingested MCP connectors stay editable (they own the URL). */}
        <Text style={styles.label}>Server URL</Text>
        {m.managed ? (
          <View style={[styles.input, styles.inputLocked]}>
            <Text className="t-mono" style={styles.inputLockedText}>{m.url}</Text>
          </View>
        ) : (
          <TextInput value={m.url} onChangeText={v => actions.mcpManageSet({ url: v })} style={styles.input} />
        )}
        {m.managed && <Text style={styles.managedHint}>Managed by Softknack — connect via OAuth. The server URL isn’t editable.</Text>}

        <View style={styles.split}>
          <View style={{ flex: 1 }}>
            <Text style={styles.label}>Transport</Text>
            <View style={styles.cdRow}>
              <Pressable disabled={m.managed} onPress={() => !m.managed && actions.mcpManageSet({ transport: 'sse' })} style={[styles.cdBtn, { borderColor: m.transport === 'sse' ? colors.accent : colors.line } as any, m.managed && styles.cdBtnLocked]}><Text style={styles.cdText}>SSE</Text></Pressable>
              <Pressable disabled={m.managed} onPress={() => !m.managed && actions.mcpManageSet({ transport: 'stdio' })} style={[styles.cdBtn, { borderColor: m.transport === 'stdio' ? colors.accent : colors.line } as any, m.managed && styles.cdBtnLocked]}><Text style={styles.cdText}>streamable-http</Text></Pressable>
            </View>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.label}>Product type</Text>
            <Select value={m.category} options={catOptions} onChange={v => actions.mcpManageSet({ category: v, extraTool: false })} variant="field" height={38} />
          </View>
        </View>

        <View style={styles.toolsHeader}>
          <Text style={styles.toolsLabel}>{loading ? 'Discovering tools…' : 'Discovered tools · ' + tools.length + ' tools'}</Text>
          <Pressable onPress={actions.resyncMcp} style={styles.resync}>
            {React.createElement('div', { className: m.resyncing ? 't-anim-spin' : undefined, style: { display: 'inline-flex' } }, <Refresh size={12} color={colors.ink2} />)}
            <Text style={styles.resyncText}>{m.resyncing ? 'Re-syncing…' : 'Re-sync tools'}</Text>
          </Pressable>
        </View>
        <View style={{ gap: 6 }}>
          {tools.map((t: any, i: number) => (
            <View key={i} style={styles.tool}>
              <View style={styles.toolDot} />
              <Text className="t-mono" style={[styles.toolName, { flex: 1 }]}>{t.name}</Text>
              {t.isNew && <View style={styles.newBadge}><Text style={styles.newBadgeText}>new</Text></View>}
              <Text style={styles.toolKind}>{t.kind}</Text>
            </View>
          ))}
        </View>
      </ScrollView>

      <View style={styles.footer}>
        {/* This only removes the MCP server wiring (DELETE /integrations/:id/mcp),
            NOT the connection. Labeled accordingly so it isn't mistaken for a true
            disconnect — that lives in the detail modal as "Disconnect" (revoke). */}
        <View style={{ flexDirection: 'row', gap: 10 }}>
          <Button label="Remove MCP config" variant="ghost" onPress={actions.disconnectMcp} />
          {/* Mapping Plane: propose canonical→vendor operation mappings with AI. */}
          <Button label="Map with AI" variant="secondary" onPress={() => actions.openMapAi(m.id)} />
        </View>
        <View style={{ flexDirection: 'row', gap: 10 }}>
          <Button label="Cancel" variant="ghost" onPress={actions.closeMcpManage} />
          {/* Save only persists Server URL + Transport — both read-only for managed
              connectors, so there's nothing to save (and the BE would reject it).
              Hidden for managed; Re-sync tools remains the useful action. */}
          {!m.managed && <Button label="Save changes" onPress={actions.saveMcpManage} />}
        </View>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  header: { padding: 18, paddingHorizontal: 20, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', alignItems: 'center', gap: 12 },
  avatar: { width: 38, height: 38, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, color: '#fff' },
  name: { fontFamily: fontFamily.sans, fontWeight: fontWeight.semibold, fontSize: fontSize.lg, color: colors.ink },
  sub: { fontFamily: fontFamily.mono, fontSize: fontSize.xs, color: colors.ink3 },
  connected: { flexDirection: 'row', alignItems: 'center', gap: 6, height: 24, paddingHorizontal: 10, borderRadius: radii.full, borderWidth: 1, borderColor: colors.successLine, backgroundColor: colors.successSoft },
  connectedDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: colors.success },
  connectedText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: colors.success },
  infoBox: { padding: 11, paddingHorizontal: 13, borderWidth: 1, borderColor: colors.infoLine, backgroundColor: colors.infoSoft, borderRadius: radii.r3, marginBottom: 18 },
  infoText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.info, lineHeight: 18 },
  label: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium, marginBottom: 6 },
  input: { width: '100%', height: 38, paddingHorizontal: 12, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, borderRadius: radii.r2, fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink as any, outlineStyle: 'none' } as any,
  // Read-only Server URL for managed connectors: greyed, non-editable surface.
  inputLocked: { backgroundColor: colors.paper2, justifyContent: 'center' as any },
  inputLockedText: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink3 },
  managedHint: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, marginTop: 6, lineHeight: 16 },
  cdBtnLocked: { opacity: 0.55, cursor: 'not-allowed' } as any,
  split: { flexDirection: 'row', gap: 16, marginTop: 16 },
  cdRow: { flexDirection: 'row', gap: 8 },
  cdBtn: { flex: 1, paddingVertical: 9, borderRadius: radii.r2, borderWidth: 1.5, backgroundColor: colors.paper2, alignItems: 'center', cursor: 'pointer' } as any,
  cdText: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink },
  toolsHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 20, marginBottom: 8 },
  toolsLabel: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, textTransform: 'uppercase', letterSpacing: 0.5, color: colors.ink3, fontWeight: fontWeight.semibold },
  resync: { height: 28, paddingHorizontal: 11, borderRadius: radii.r2, borderWidth: 1, borderColor: colors.lineStrong, backgroundColor: colors.card, flexDirection: 'row', alignItems: 'center', gap: 6, cursor: 'pointer' } as any,
  resyncText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2 },
  tool: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 9, paddingHorizontal: 12, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, backgroundColor: colors.paper2 },
  toolDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: colors.success },
  toolName: { fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.ink },
  newBadge: { paddingHorizontal: 4, borderRadius: 3, borderWidth: 1, borderColor: colors.successLine },
  newBadgeText: { fontFamily: fontFamily.sans, fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: fontWeight.semibold, color: colors.success },
  toolKind: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3 },
  footer: { padding: 14, paddingHorizontal: 20, borderTopWidth: 1, borderTopColor: colors.line, backgroundColor: colors.paper2, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
});
